import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router';
import type { ArtifactKind } from '@inference.engine/widgets';
import { useQuery } from '@tanstack/react-query';
import { projectArtifactsQuery, type ProjectArtifact } from '@/entities/project-artifact';
import { projectFoldersQuery } from '@/entities/project-folder';
import { useProjectCapabilities } from '@/entities/user';
import {
  LeavesTogether,
  SETTLED_EXIT_SECONDS,
  SettledContent,
  SETTLED_STATE,
} from '@/shared/ui/animations';
import { useDelayedFlag } from '@/shared/hooks';
import { PaginationControls } from '@/shared/ui';
import { DeleteConfirmationModal } from '@/shared/ui/DeleteConfirmationModal';
import { SEARCH_DEBOUNCE_MS } from '@/shared/constants/search.constants';
import { useDebounce, useModalState } from '@/shared/hooks';
import type { ProjectDetailOutletContext } from '../../projectDetail.types';
import { AddArtifactsModal } from './components/AddArtifactsModal';
import { ArtifactPreviewModal } from './components/ArtifactPreviewModal';
import { ArtifactsEmptyState } from './components/ArtifactsEmptyState';
import { ArtifactsGrid } from './components/ArtifactsGrid';
import { ArtifactsToolbar, FOLDER_FILTER_ROOT_VALUE } from './components/ArtifactsToolbar';
import { useLibraryDelete } from './hooks/useLibraryDelete';
import { LIBRARY_PAGE_SIZE, resolveArtifactSort } from './library.constants';
import { useArtifactPreviewStore } from './store/artifactPreview.store';
import { useTranslation } from 'react-i18next';

/**
 * Translate the toolbar's `folderFilter` (UI sentinel) to the BE query param.
 *
 *   null                       → undefined  (no filter — all folders + root)
 *   FOLDER_FILTER_ROOT_VALUE   → ''         (BE convention: empty string means root only)
 *   <uuid>                     → <uuid>     (specific folder)
 */
function toFolderQuery(folderFilter: string | null): string | undefined {
  if (folderFilter === null) return undefined;
  if (folderFilter === FOLDER_FILTER_ROOT_VALUE) return '';
  return folderFilter;
}

export function Library() {
  const { t } = useTranslation('projects');

  const { project } = useOutletContext<ProjectDetailOutletContext>();
  const projects = useProjectCapabilities();
  const projectId = project.platformProjectId;
  const [kind, setKind] = useState<ArtifactKind | null>(null);
  const [folderFilter, setFolderFilter] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [sort, setSort] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [artifactToDelete, setArtifactToDelete] = useState<ProjectArtifact | null>(null);
  const addArtifactsModal = useModalState();

  const { confirmDelete, isPending: isDeleting } = useLibraryDelete({
    projectId: projectId,
    onCleared: () => setArtifactToDelete(null),
  });

  const debouncedSearch = useDebounce(searchInput, SEARCH_DEBOUNCE_MS);
  const trimmedSearch = debouncedSearch.trim();

  // Reset to first page whenever any filter changes so users don't land on an
  // out-of-range page after the result set shrinks/grows. Wired through
  // dedicated handlers below instead of a derived-value `useEffect` (no-use-effect).
  const handleKindChange = (next: ArtifactKind | null) => {
    setKind(next);
    setPage(1);
  };
  const handleFolderFilterChange = (next: string | null) => {
    setFolderFilter(next);
    setPage(1);
  };
  const handleSearchChange = (next: string) => {
    setSearchInput(next);
    setPage(1);
  };
  const handleSortChange = (next: string | null) => {
    setSort(next);
    setPage(1);
  };
  // Bulk reset for the no-results "Clear filters" action — mirrors the
  // individual toolbar handlers above (each of which also resets the page).
  const handleClearFilters = () => {
    setSearchInput('');
    setKind(null);
    setFolderFilter(null);
    setPage(1);
  };

  const take = LIBRARY_PAGE_SIZE;
  const skip = (page - 1) * take;

  const sortParams = resolveArtifactSort(sort);

  const listQuery = useMemo(
    () => ({
      skip,
      take,
      kind: kind ?? undefined,
      folderId: toFolderQuery(folderFilter),
      search: trimmedSearch || undefined,
      sortBy: sortParams?.sortBy,
      sortOrder: sortParams?.sortOrder,
    }),
    [skip, take, kind, folderFilter, trimmedSearch, sortParams?.sortBy, sortParams?.sortOrder],
  );

  // Publish the active grid query to the preview store so
  // `useArtifactNavigation` can read the exact cache entry the user is
  // viewing instead of guessing across stale filter combos.
  //
  // `no-use-effect` knowingly violated here: the alternative (moving
  // filter/sort/page state INTO the preview store + deriving listQuery from
  // it) is a non-trivial refactor that couples grid state to a Library-page
  // store. Until that lands, this effect is the cheapest bridge — single
  // dependency (listQuery), single setter, cleanup unmounts the publication.
  // Tracked separately for follow-up cleanup.
  const setListQuery = useArtifactPreviewStore((s) => s.setListQuery);

  useEffect(() => {
    setListQuery(listQuery);
    return () => setListQuery(null);
  }, [listQuery, setListQuery]);

  const { data, isLoading, isPending, isError } = useQuery(
    projectArtifactsQuery(projectId, listQuery),
  );

  const { data: foldersData } = useQuery(projectFoldersQuery(projectId));

  const folderOptions = useMemo(
    () => (foldersData?.items ?? []).map((folder) => ({ id: folder.id, name: folder.name })),
    [foldersData],
  );

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const hasRows = items.length > 0;
  const hasFilter = kind !== null || folderFilter !== null || trimmedSearch.length > 0;
  // Never show the add invite on a failed load — error UI itself is MFINS-3106's
  // scope. `isPending` (not `isLoading`) so a disabled/idle query can't flash
  // a false true-empty — parity with the Trackers gate.
  const showEmpty = total === 0 && !isPending && !isError;
  const isUploadRow = artifactToDelete?.origin === 'upload';

  /**
   * Toolbar visibility mirrors the Briefs list: show it when there are rows
   * (data), while loading, or when a search/filter is active (so the user can
   * clear a zero-match filter). Hide it for the true-empty state — with no
   * artifacts to search or filter, the controls would be noise.
   */
  // The truth about loading, and separately whether it has lasted long enough
  // to be worth drawing. Reading the delayed flag as the truth calls an
  // unanswered query settled for its first quarter second, which renders an
  // empty grid before the answer is in.
  const isLoadingEmpty = isLoading && !hasRows;
  const showLoading = useDelayedFlag(isLoadingEmpty);
  // Gated on the indicator, not on raw loading: the toolbar and the skeletons
  // are one picture and should arrive together, so a fast answer shows neither.
  const showToolbar = hasRows || showLoading || hasFilter;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      {/* Only the toolbar rides the section's cascade; the content below
          settles into one of several states and crossfades once. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg bg-card">
        <LeavesTogether present={showToolbar} exitSeconds={SETTLED_EXIT_SECONDS}>
          <ArtifactsToolbar
            pending={showLoading}
            kindFilter={kind}
            onKindFilterChange={handleKindChange}
            folderFilter={folderFilter}
            onFolderFilterChange={handleFolderFilterChange}
            folders={folderOptions}
            search={searchInput}
            onSearchChange={handleSearchChange}
            sort={sort}
            onSortChange={handleSortChange}
            onAddArtifactsClick={addArtifactsModal.open}
          />
        </LeavesTogether>

        <SettledContent
          state={
            isLoadingEmpty
              ? SETTLED_STATE.loading
              : showEmpty
                ? SETTLED_STATE.empty
                : SETTLED_STATE.rows
          }
          className="min-h-0 flex-1 overflow-auto px-section pb-section"
          contentClassName="flex min-h-0 flex-col"
        >
          {isLoadingEmpty && !showLoading ? null : showEmpty ? (
            <ArtifactsEmptyState
              variant={hasFilter ? 'no-results' : 'empty'}
              onAddArtifacts={addArtifactsModal.open}
              onClearFilters={handleClearFilters}
            />
          ) : (
            <ArtifactsGrid
              projectId={projectId}
              artifacts={items}
              isLoading={showLoading}
              onDeleteArtifact={setArtifactToDelete}
              deleteDisabled={!projects.canUploadAssets}
            />
          )}
        </SettledContent>

        {addArtifactsModal.isMounted && (
          <AddArtifactsModal
            key={addArtifactsModal.instanceKey}
            open={addArtifactsModal.isOpen}
            onClose={addArtifactsModal.close}
            projectId={projectId}
          />
        )}

        <ArtifactPreviewModal projectId={projectId} />

        <DeleteConfirmationModal
          open={artifactToDelete !== null}
          isDeleting={isDeleting}
          customModalTitle={
            isUploadRow
              ? t('projectDetail.library.deleteFile')
              : t('projectDetail.library.removeFromProject')
          }
          resourceName={artifactToDelete?.artifactName}
          warningMessage={
            isUploadRow
              ? t('projectDetail.library.thisPermanentlyDeletesTheFile')
              : t('projectDetail.library.theArtifactStaysInYour')
          }
          onCancel={() => setArtifactToDelete(null)}
          onConfirmDelete={() => {
            if (artifactToDelete) confirmDelete(artifactToDelete);
          }}
        />

        <PaginationControls
          page={page}
          totalItems={total}
          pageSize={take}
          onPageChange={setPage}
          className="p-4"
        />
      </div>
    </div>
  );
}
